import React, { useEffect, useState } from 'react'
import { Dispatch } from 'redux'
import { connect, useDispatch, useStore, useSelector } from 'react-redux'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import { LoadingDots } from '@chegg-wt/core/components/LoadingDots'
import { UserRole } from '@chegg-wt/core/types/auth'
import { UserRoles } from '@chegg-wt/core/constants/user'
import { navigated } from '@chegg-wt/core/state/app/actions'
import { openModal, closeModal } from '@chegg-wt/core/state/modals/actions'
import { EXPORT_MODAL, ONE_AUTH_MODAL } from '@chegg-wt/core/state/modals/constants'
import { OneAuth as BaseOneAuth, ClientId, Options } from '@chegg/one-auth'
import { publicUrl } from '@chegg-wt/core/utils/url'
import { getRioConfig } from '@chegg-wt/core/state/instrumentation/rio/utils/rioConfig'
import useOneAuthEventsHandler from '@chegg-wt/core/hooks/useOneAuthEventHandler'
import {
  isAuthLoadingSelector,
  userRoleSelector,
} from '@chegg-wt/core/state/user/selectors'
import { modalDataSelector } from '@chegg-wt/core/state/modals/selectors'
import { selectedCitationsSelector } from '@chegg-wt/core/state/bibliography/selectors'
import {
  formPageAuthComplete,
  writingFormSubmit,
} from '@chegg-wt/core/state/form/actions'
import useOneAuthCallback from '@chegg-wt/core/hooks/useAuthCallback'
import { Config } from '@chegg-wt/core/config'
import { getProfileCode } from '@chegg-wt/core/components/Auth/OneAuth/utils'
import { getCitationsFromSessionStorage } from '@chegg-wt/core/utils/writingForms'
import { AdUnlockVariations, AppRoute, AppRouteOpts } from '@chegg-wt/core/types/core'
import { adUnlockVariationSelector } from '@chegg-wt/core/state/createCitation/selectors'
import { setCookie, Cookies } from '@chegg-wt/core/utils/cookies'
import Modal from '..'
import * as Styled from './styled'
import { AuthModalData, AuthModalOrigin } from './types'

export const iframeTestId = 'test-authiframe'

interface OneAuthModalProps {
  isLoading: boolean
  userRole: UserRole
  adUnlockModalExpVariant: AdUnlockVariations
  closeModal: (origin?: string) => void
  navigated: (route: AppRoute, opts?: AppRouteOpts) => void
}

const OneAuthModal: React.FC<OneAuthModalProps> = ({
  userRole,
  isLoading,
  adUnlockModalExpVariant,
  closeModal,
  navigated,
}) => {
  const dispatch = useDispatch()
  const { toProfile } = useRoutesContext()

  const state = useStore().getState()
  const data = useSelector(modalDataSelector) as AuthModalData
  const selectedCitations = useSelector(selectedCitationsSelector)

  const { defaultFlow } = useSelector(modalDataSelector) as AuthModalData
  const setViewedAdUnlockCookie = () => {
    if (
      adUnlockModalExpVariant === AdUnlockVariations.VARIANT1 ||
      adUnlockModalExpVariant === AdUnlockVariations.VARIANT2
    ) {
      const hours = 12
      const expiryDate = new Date(new Date().getTime() + hours * 60 * 60 * 1000)
      setCookie(Cookies.ViewedAdUnlock, 'true', { expires: expiryDate })
    }
  }

  // Optimistically set to true as user is usually still fetching when page mounts.
  // If it turns out they are logged out then we will change value to false
  const [isAuthorizedOnEntry, setIsAuthorizedOnEntry] = useState(true)
  const [authSuccess, setAuthSuccess] = useState(false)
  useOneAuthEventsHandler({ closeModal })

  useEffect(() => {
    if (userRole && userRole === UserRoles.NOT_LOGGED_IN) {
      setIsAuthorizedOnEntry(false)
    } else if (userRole && isAuthorizedOnEntry) {
      navigated(toProfile())
    }
  }, [userRole])

  useEffect(() => {
    document.body.style.removeProperty('overflow')
  }, [])

  useEffect(() => {
    if (!isLoading && authSuccess) {
      closeModal()
      switch (data.origin) {
        case AuthModalOrigin.VIEW_PAGE_EXPORT:
          dispatch(openModal({ name: EXPORT_MODAL, data: { selectedCitations } }))
          break
        case AuthModalOrigin.CITATION_FORM_PAGE: {
          setViewedAdUnlockCookie()
          const writingFormsCitationData = getCitationsFromSessionStorage()
          const { citation, initialCitationData } = writingFormsCitationData
          dispatch(writingFormSubmit({ citation, initialCitationData }))
          break
        }
        case AuthModalOrigin.REDESIGNED_UNLOCK_MODAL:
          dispatch(formPageAuthComplete({ alwaysParticipate: data.alwaysParticipate }))
          break
        default:
          break
      }
    }
  }, [isLoading, authSuccess])

  const callback = useOneAuthCallback(setAuthSuccess, dispatch)
  const rioConfig = getRioConfig(state, '')

  const oneAuthOptions: Options = {
    footer: { image: publicUrl('/static/logo.svg') },
    signup: {
      title: data.title,
      form: {
        credentials: {
          ssoProviders: ['apple', 'facebook', 'google'],
        },
        userInfo: {
          student: {
            highschool: {
              highschoolGradYearSelect: true,
            },
            college: {
              collegeSelect: {
                yearInSchool: true,
              },
            },
          },
          parent: {},
        },
      },
    },
  }

  return (
    <Modal
      transparentBackground
      showModal
      outsideClick
      noPadding
      fullWidth={false}
      closeButton
      fullMobileModal
      isLoading={isLoading}
      zIndex={3000}
      maxHeight
    >
      {isLoading && (
        <Styled.Loading>
          <LoadingDots />
        </Styled.Loading>
      )}
      {!isLoading && !authSuccess && (
        <Styled.OneAuthWrapper>
          <BaseOneAuth
            clientId={getProfileCode(Config.tokenSiteCode) as ClientId}
            callback={callback}
            rioConfig={rioConfig}
            view={defaultFlow || 'signin'}
            className="no-override"
            options={oneAuthOptions}
          />
        </Styled.OneAuthWrapper>
      )}
    </Modal>
  )
}

const mapStateToProps = (state) => ({
  userRole: userRoleSelector(state),
  isLoading: isAuthLoadingSelector(state),
  adUnlockModalExpVariant: adUnlockVariationSelector(state),
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  closeModal: (origin?: string) => {
    dispatch(closeModal(ONE_AUTH_MODAL, origin))
  },
  navigated: (route: AppRoute, opts?: AppRouteOpts) => {
    dispatch(navigated(route, opts))
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(OneAuthModal)
